import { forwardRef, useState, useEffect, useImperativeHandle } from 'react';
import type { PropsWithoutRef, MouseEvent } from 'react';

import {
  Text,
  Flex,
  Icon,
  Card,
  CardHeader,
  CardContent,
  useScrollToggle,
  useElement,
  useOuterEvent,
  EmptyState,
  useConsolidatedRef
} from '@pega/cosmos-react-core';

import ContextMenuPopover from './ContextMenuPopover';
import { StyledEntityList } from './Email.styles';
import EmailEntity from './EmailEntity';
import type { ContextMenuProps, EntityListProps, TargetProps } from './Email.types';

const EntityList = forwardRef(function EntityList(
  { content, header, contextMenu }: PropsWithoutRef<EntityListProps>,
  ref: EntityListProps['ref']
) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [currentTarget, setCurrentTarget] = useState<TargetProps | undefined>();
  const { disableScroll, enableScroll } = useScrollToggle();
  // Only way to set this is through imperative handle
  const [contextMenuItems, setContextMenuItems] = useState<ContextMenuProps['items']>([]);
  const [contextMenuLoading, setContextMenuLoading] = useState<boolean>(false);
  const [popoverEl, setPopoverEl] = useElement<HTMLDivElement>();
  const popoverRef = useConsolidatedRef(setPopoverEl, contextMenu?.popoverRef);

  useOuterEvent('mousedown', [popoverEl], () => {
    if (popoverOpen) setPopoverOpen(false);
  });

  useImperativeHandle(contextMenu?.handle, () => ({
    setItems: (ctxMenuItems: ContextMenuProps['items']) => {
      setContextMenuItems(ctxMenuItems || []);
    },
    setLoading(loading) {
      setContextMenuLoading(loading);
    },
    setOpen(visible) {
      setPopoverOpen(visible);
    }
  }));

  // Handler for right click
  const onContextMenu = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && e.target.textContent?.trim()) {
      setCurrentTarget({
        targetNode: e.target,
        cursorPosition: {
          x: e.pageX - window.scrollX,
          y: e.pageY - window.scrollY
        }
      });
      contextMenu?.onContextMenu(e);
    }
  };

  const onItemClick: ContextMenuProps['onItemClick'] = (selectedValue: {
    fieldName: string;
    fieldValue: string;
  }) => {
    setPopoverOpen(false);
    contextMenu?.onItemClick(selectedValue);
  };

  const fields = content.map(data => {
    return data.value
      ? {
          ...data,
          value: (
            // Attach the handler only when context menu is enabled
            <Flex container={{ gap: 0.5, wrap: 'wrap' }} onContextMenu={onContextMenu}>
              {data.value.map(entityObject => {
                return <EmailEntity key={entityObject.id} entity={entityObject.entity} />;
              })}
            </Flex>
          )
        }
      : { ...data, value: undefined };
  });

  useEffect(() => {
    if (popoverOpen) disableScroll();
    else enableScroll();
  }, [popoverOpen]);

  return (
    <Card ref={ref}>
      {header && (
        <CardHeader container={{ alignItems: 'center', gap: 1 }}>
          <Icon name={header.icon} />
          <Text variant='h3'>{header.text}</Text>
        </CardHeader>
      )}
      {content.length > 0 ? (
        <CardContent>
          <StyledEntityList fields={fields} variant='stacked' />
          {contextMenu && currentTarget && (
            <ContextMenuPopover
              ref={popoverRef}
              cursorPosition={currentTarget?.cursorPosition}
              contextMenu={{
                ...contextMenu,
                items: contextMenuItems,
                loading: contextMenuLoading,
                onItemClick
              }}
              targetNode={currentTarget.targetNode}
              show={popoverOpen}
            />
          )}
        </CardContent>
      ) : (
        <EmptyState />
      )}
    </Card>
  );
});

export default EntityList;
